/**
 * Les deux chronomètres de la séance (`ARCHITECTURE_V2.md` §4.5, specs §12.3).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 HORODATAGE ABSOLU, JAMAIS UN DÉCRÉMENT
 * ─────────────────────────────────────────────────────────────────────────
 *
 * C'est une décision V2 « à ne pas défaire », et la raison est concrète : **iOS
 * gèle l'onglet dès que l'écran se verrouille**, c'est-à-dire l'état normal
 * d'un téléphone posé entre deux séries. Un compteur qui décrémente
 * (`reste -= 1` à chaque tick) s'arrête avec lui et reprend là où il en était :
 * après trois minutes de poche, il annonce trente secondes de repos alors que
 * trois minutes ont passé.
 *
 * Ici, **rien n'est compté** : on garde un instant (`Date.now()` au départ, ou
 * l'instant de fin visé) et chaque rendu **relit l'horloge**. Le `setInterval`
 * ne sert qu'à provoquer un rendu ; s'il rate cent battements parce que
 * l'onglet dormait, la valeur affichée au réveil est **juste** — il n'y a rien
 * à rattraper, puisque rien n'a été accumulé.
 *
 * ⚠️ **`visibilitychange` en plus du `setInterval`**, et ce n'est pas une
 * ceinture de sécurité : au retour au premier plan, le premier battement peut
 * arriver jusqu'à une seconde plus tard. Sans cet écouteur, on verrait la
 * vieille valeur pendant ce délai — c'est-à-dire précisément à l'instant où
 * l'utilisateur regarde son téléphone pour savoir s'il doit repartir.
 *
 * ⚠️ **Le chronomètre de repos ne vit qu'en mémoire, rien en base** (§4.5) :
 * un repos repris trois heures plus tard n'est pas un repos. Il ne survit donc
 * volontairement pas à une fermeture de l'application — contrairement à la
 * séance elle-même, qui doit survivre à tout.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Provoque un rendu à chaque seconde, tant que `actif`.
 *
 * ⚠️ **Un seul battement partagé par les deux chronomètres**, plutôt qu'un
 * `setInterval` chacun : deux minuteries indépendantes dérivent l'une de
 * l'autre et font clignoter les deux nombres à des instants différents, ce qui
 * se voit tout de suite sur une barre qui en affiche deux côte à côte.
 */
function useTick(actif: boolean): number {
  /*
   * ⚠️ **L'instant est un ÉTAT, il n'est pas relu au rendu.** Un `return
   * Date.now()` donnerait la même valeur à l'écran, mais rendrait ce hook
   * impur : deux rendus successifs déclenchés par autre chose (une réponse
   * serveur, une frappe) renverraient deux valeurs différentes sans qu'aucun
   * état n'ait changé, ce que `react-hooks/purity` refuse à juste titre.
   *
   * 🔴 **Et ça ne change rien à la propriété qui compte** : la valeur reste
   * calculée depuis un horodatage **absolu**, jamais accumulée. Le battement
   * ne fait que rafraîchir la lecture ; s'il en rate cent parce que l'onglet
   * dormait, la première lecture au réveil est déjà juste.
   */
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!actif) return;

    const battre = () => {
      setNow(Date.now());
    };
    /* Une première lecture immédiate : le montage peut suivre un long sommeil. */
    battre();
    const timer = window.setInterval(battre, 1000);
    /*
     * ⚠️ Au retour au premier plan, on ne peut pas attendre le prochain
     * battement — cf. l'en-tête.
     */
    document.addEventListener('visibilitychange', battre);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', battre);
    };
  }, [actif]);

  return now;
}

/**
 * Secondes écoulées depuis `startedAt` — le chronomètre de séance.
 *
 * ⚠️ **Il s'arrête à `finishedAt` quand la séance est close**, plutôt que de
 * continuer à courir sur une séance terminée : relire une séance de la semaine
 * dernière doit afficher sa durée, pas le temps écoulé depuis.
 */
export function useElapsed(startedAt: number | null, finishedAt: number | null): number {
  const now = useTick(startedAt !== null && finishedAt === null);
  /*
   * ⚠️ **`null` est accepté, et rend zéro.** L'appelant ne connaît pas encore
   * la séance au premier rendu (le cache se restaure, ou la requête est en
   * vol), et un hook ne peut pas être conditionnel. Lui faire passer
   * `Date.now()` en attendant aurait été impur *et* faux — l'écoulement aurait
   * démarré à zéro puis sauté à sa vraie valeur.
   */
  if (startedAt === null) return 0;
  const fin = finishedAt ?? now;
  return Math.max(0, Math.floor((fin - startedAt) / 1000));
}

export interface RestTimer {
  /** Secondes restantes, ou `null` si aucun repos n'est en cours. */
  remaining: number | null;
  /** Durée du repos en cours, pour dessiner une progression. */
  total: number | null;
  /** Le repos est écoulé — il reste affiché, en négatif, jusqu'au prochain geste. */
  overdue: boolean;
  start: (seconds: number) => void;
  stop: () => void;
}

/**
 * Le chronomètre de repos, déclenché à la validation d'une série.
 *
 * 🔴 **On garde l'instant de FIN, pas une durée qui s'épuise.** Même
 * raisonnement que ci-dessus, et c'est ici qu'il compte le plus : le repos est
 * précisément le moment où le téléphone est posé, écran éteint.
 *
 * ⚠️ **Il continue en négatif au lieu de s'effacer.** Un chronomètre qui
 * disparaît à zéro ne dit pas si l'on est reparti à l'heure ou avec deux
 * minutes de retard — et c'est une information qu'on veut en relevant les yeux.
 * Il s'efface au geste suivant, pas tout seul.
 */
export function useRestTimer(): RestTimer {
  const [fin, setFin] = useState<number | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const now = useTick(fin !== null);

  const start = useCallback((seconds: number) => {
    setFin(Date.now() + seconds * 1000);
    setTotal(seconds);
  }, []);

  const stop = useCallback(() => {
    setFin(null);
    setTotal(null);
  }, []);

  const remaining = fin === null ? null : Math.round((fin - now) / 1000);

  return useMemo(
    () => ({
      remaining,
      total,
      overdue: remaining !== null && remaining < 0,
      start,
      stop,
    }),
    [remaining, total, start, stop],
  );
}

/**
 * Maintient l'écran allumé pendant la séance, **là où la plateforme le
 * fournit** (§4.5).
 *
 * ⚠️ **Sous détection, et jamais appelé sans garde** — la discipline de
 * `lib/haptics.ts`, écrite après le constat que `navigator.vibrate` **lève**
 * sur un moteur qui ne l'implémente pas. Ici l'API est asynchrone et peut
 * rejeter pour une raison parfaitement normale (onglet en arrière-plan,
 * batterie faible) : un rejet non capté remonterait en erreur non gérée dans
 * la console, pour une fonction de confort.
 *
 * ⚠️ **Relâché à la fin de la séance et au démontage**, comme le `MediaStream`
 * du scanner (§6.2) : une ressource prise par un écran se rend quand l'écran
 * s'en va. Un verrou oublié garderait l'écran allumé jusqu'à la fermeture de
 * l'application, et viderait la batterie en salle.
 *
 * ⚠️ **Repris au retour au premier plan** : iOS relâche le verrou de lui-même
 * quand l'onglet passe en arrière-plan, et ne le rend jamais tout seul. Sans
 * cet écouteur, l'écran resterait allumé jusqu'au premier verrouillage, puis
 * plus jamais — un comportement à moitié présent, plus déroutant qu'absent.
 */
export function useWakeLock(actif: boolean): void {
  const verrou = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!actif) return;
    if (!('wakeLock' in navigator)) return;

    let vivant = true;

    const prendre = () => {
      if (!vivant || document.visibilityState !== 'visible') return;
      if (verrou.current !== null) return;
      void navigator.wakeLock
        .request('screen')
        .then((sentinel) => {
          if (!vivant) {
            void sentinel.release();
            return;
          }
          verrou.current = sentinel;
          sentinel.addEventListener('release', () => {
            verrou.current = null;
          });
        })
        /* Un refus est nominal — cf. l'en-tête. On n'en fait rien. */
        .catch(() => undefined);
    };

    prendre();
    document.addEventListener('visibilitychange', prendre);

    return () => {
      vivant = false;
      document.removeEventListener('visibilitychange', prendre);
      const pris = verrou.current;
      verrou.current = null;
      if (pris) void pris.release().catch(() => undefined);
    };
  }, [actif]);
}

/**
 * « 1:04:12 » ou « 4:12 » — l'heure n'apparaît que si elle est non nulle.
 *
 * ⚠️ **Distinct de `formatSeconds` de `routineDraft.ts`, et volontairement.**
 * Celui-là écrit « 1 min 30 » pour un repos *prescrit* — une consigne qu'on
 * lit. Celui-ci écrit « 1:30 » pour un temps *qui court* : c'est la forme d'un
 * chronomètre, et les deux se lisent d'un coup d'œil dans leur registre. Ce
 * n'est pas le doublon que l'étape 5 a évité (deux formatages des mêmes
 * secondes sur le même écran), c'est deux objets différents.
 */
export function formatClock(seconds: number): string {
  const negatif = seconds < 0;
  const abs = Math.abs(seconds);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  const deux = (n: number) => String(n).padStart(2, '0');
  const corps = h > 0 ? `${String(h)}:${deux(m)}:${deux(s)}` : `${String(m)}:${deux(s)}`;
  return negatif ? `−${corps}` : corps;
}
